'use client';

import React from 'react';
import Image from 'next/image';

interface HeroSectionProps {
  title: string | React.ReactNode;
  description: string;
  imageSrc: string;
  imageAlt: string;
  className?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  className = '',
}) => {
  return (
    <section className={`relative h-[320px] sm:h-[400px] md:h-[450px] lg:h-[500px] overflow-hidden ${className}`}>
      {/* Permanent visible background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] to-[#1e293b]" />
      
      {/* Background Image with pure CSS animation - no JavaScript state */}
      <div className="absolute inset-0 overflow-hidden">
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          className="object-cover opacity-90 animate-continuous-zoom"
          priority
          quality={90}
          sizes="100vw"
          unoptimized={true}
        />
      </div>

      {/* Gradient overlays for depth and text clarity */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a]/40 via-[#0f172a]/20 to-[#0f172a]/60" />
      
      {/* Subtle vignette effect */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(15,23,42,0.6)_100%)]" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10 h-full flex items-center justify-center">
        <div className="max-w-4xl mx-auto text-center px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            {title}
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
            {description}
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;